/**
 * Fair Mechanic AI — Main React Application
 * Single-page app: Upload → Loading → Results
 * 
 * SETUP:
 *   npx create-react-app carcheck-frontend
 *   Replace src/App.js with this file
 *   npm install axios
 */

import { useState, useCallback } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─── Color constants ───────────────────────
const VERDICT_CONFIG = {
  FAIR:      { color: '#1a7a4a', bg: '#e2f2ea', label: 'Fair Price',  icon: '✅' },
  OVERPRICED:{ color: '#b05a10', bg: '#fdf0e0', label: 'Overpriced',  icon: '⚠️' },
  CAN_WAIT:  { color: '#b05a10', bg: '#fdf0e0', label: 'Can Wait',    icon: '🕐' },
  SKIP:      { color: '#c02020', bg: '#fce8e8', label: 'Skip It',     icon: '❌' },
  URGENT:    { color: '#c02020', bg: '#fce8e8', label: 'Urgent',      icon: '🚨' },
  UNKNOWN:   { color: '#666',    bg: '#f5f5f5', label: 'Review',      icon: '❓' }
};

// ─────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────
export default function App() {
  const [stage, setStage] = useState('upload'); // upload | loading | results | error
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ vin: '', year: '', make: '', model: '', mileage: '', zip: '' });
  const [vehicleMode, setVehicleMode] = useState('vin');
  // expose to UploadStage via props below
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [inputMode, setInputMode] = useState('file'); // file | text
  const [pastedText, setPastedText] = useState('');

  const handleFileSelect = useCallback((f) => {
    if (!f) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(f.type)) {
      setError('Please upload a PDF, JPG, or PNG file');
      return;
    }
    setFile(f);
    setError(null);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect]);

  const handleSubmit = async () => {
    if (inputMode === 'file' && !file) {
      setError('Please upload a file or paste your quote text');
      return;
    }
    if (inputMode === 'text' && !pastedText.trim()) {
      setError('Please paste your quote text');
      return;
    }

    setStage('loading');
    setError(null);

    try {
      let response;

      if (inputMode === 'file') {
        const fd = new FormData();
        fd.append('quote', file);
        if (formData.vin) fd.append('vin', formData.vin);
        if (formData.year) fd.append('year', formData.year);
        if (formData.make) fd.append('make', formData.make);
        if (formData.model) fd.append('model', formData.model);
        if (formData.mileage) fd.append('mileage', formData.mileage);
        if (formData.zip) fd.append('zip', formData.zip);

        response = await axios.post(`${API_BASE}/api/analyze`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${API_BASE}/api/analyze-text`, {
          text: pastedText,
          ...formData
        });
      }

      setAnalysis(response.data.analysis);
      setStage('results');
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed. Please try again.');
      setStage('upload');
    }
  };

  return (
    <div style={styles.app}>
      <Nav />
      {stage === 'upload' && (
        <UploadStage
          vehicleMode={vehicleMode}
          setVehicleMode={setVehicleMode}
          file={file}
          formData={formData}
          setFormData={setFormData}
          onFileSelect={handleFileSelect}
          onDrop={handleDrop}
          dragOver={dragOver}
          setDragOver={setDragOver}
          onSubmit={handleSubmit}
          error={error}
          inputMode={inputMode}
          setInputMode={setInputMode}
          pastedText={pastedText}
          setPastedText={setPastedText}
        />
      )}
      {stage === 'loading' && <LoadingStage />}
      {stage === 'results' && analysis && (
        <ResultsStage analysis={analysis} onReset={() => {
          setStage('upload');
          setFile(null);
          setAnalysis(null);
          setPastedText('');
        }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// NAV
// ─────────────────────────────────────────────
function Nav() {
  return (
    <nav style={styles.nav}>
      <div style={styles.navLogo}>FairMechanic<span style={{ color: '#c8401a' }}>AI</span></div>
      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
        <a href="https://fairmechanic.ai/#how-it-works" style={styles.navLink}>How it works</a>
        <a href="https://fairmechanic.ai/#pricing" style={styles.navLink}>Pricing</a>
        
      </div>
    </nav>
  );
}

// ─────────────────────────────────────────────
// UPLOAD STAGE
// ─────────────────────────────────────────────
function UploadStage({ file, formData, setFormData, onFileSelect, onDrop, dragOver, setDragOver, onSubmit, error, inputMode, setInputMode, pastedText, setPastedText, vehicleMode, setVehicleMode }) {
  return (
    <main style={styles.main}>
      <div style={styles.uploadContainer}>
        <div style={styles.uploadHeader}>
          <h1 style={styles.h1}>Is your repair quote fair?</h1>
          <p style={styles.uploadSub}>Upload your estimate and get an AI-powered second opinion in 30 seconds.</p>
        </div>

        {/* Mode toggle */}
        <div style={styles.modeToggle}>
          {['file', 'text'].map(mode => (
            <button
              key={mode}
              style={{ ...styles.modeBtn, ...(inputMode === mode ? styles.modeBtnActive : {}) }}
              onClick={() => setInputMode(mode)}
            >
              {mode === 'file' ? '📄 Upload file' : '📝 Paste text'}
            </button>
          ))}
        </div>

        {inputMode === 'file' ? (
          <div
            style={{ ...styles.dropzone, ...(dragOver ? styles.dropzoneActive : {}), ...(file ? styles.dropzoneFilled : {}) }}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
          >
            {file ? (
              <>
                <div style={{ fontSize: '2rem' }}>✅</div>
                <div style={styles.dropzoneTitle}>{file.name}</div>
                <div style={styles.dropzoneSub}>{(file.size / 1024 / 1024).toFixed(1)} MB · Ready to analyze</div>
                <label style={styles.changeFile}>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => onFileSelect(e.target.files[0])} />
                  Change file
                </label>
              </>
            ) : (
              <>
                <div style={{ fontSize: '2.5rem' }}>📋</div>
                <div style={styles.dropzoneTitle}>Drop your estimate here</div>
                <div style={styles.dropzoneSub}>PDF, JPG, or PNG · Max 20MB</div>
                <label style={styles.fileBtn}>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => onFileSelect(e.target.files[0])} />
                  Choose file
                </label>
              </>
            )}
          </div>
        ) : (
          <textarea
            style={styles.textarea}
            placeholder="Paste your repair quote here... Include all line items, prices, and any notes from the mechanic or dealer."
            value={pastedText}
            onChange={e => setPastedText(e.target.value)}
            rows={8}
          />
        )}

        {/* Optional fields */}
        <div style={styles.optionalFields}>
          <div style={styles.optionalLabel}>Optional — add for more accurate results:</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={styles.field}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                <button
                  type="button"
                  style={{ flex: 1, padding: '4px 8px', fontSize: '0.78rem', fontWeight: 600, border: 'none', borderRadius: '6px', cursor: 'pointer', background: vehicleMode === 'vin' ? '#0f0e0c' : '#f0ede6', color: vehicleMode === 'vin' ? 'white' : '#5a5850' }}
                  onClick={() => setVehicleMode('vin')}
                >VIN</button>
                <button
                  type="button"
                  style={{ flex: 1, padding: '4px 8px', fontSize: '0.78rem', fontWeight: 600, border: 'none', borderRadius: '6px', cursor: 'pointer', background: vehicleMode === 'manual' ? '#0f0e0c' : '#f0ede6', color: vehicleMode === 'manual' ? 'white' : '#5a5850' }}
                  onClick={() => setVehicleMode('manual')}
                >Year / Make / Model</button>
              </div>
              {vehicleMode === 'vin' ? (
                <input
                  style={styles.input}
                  placeholder="e.g. 1HGBH41JXMN109186"
                  value={formData.vin}
                  onChange={e => setFormData(f => ({ ...f, vin: e.target.value }))}
                  maxLength={17}
                />
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '6px' }}>
                  <input
                    style={styles.input}
                    placeholder="Year"
                    type="number"
                    value={formData.year}
                    onChange={e => setFormData(f => ({ ...f, year: e.target.value }))}
                    maxLength={4}
                  />
                  <input
                    style={styles.input}
                    placeholder="Make (Toyota)"
                    value={formData.make}
                    onChange={e => setFormData(f => ({ ...f, make: e.target.value }))}
                  />
                  <input
                    style={styles.input}
                    placeholder="Model (Camry)"
                    value={formData.model}
                    onChange={e => setFormData(f => ({ ...f, model: e.target.value }))}
                  />
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>Mileage</label>
              <input
                style={styles.input}
                placeholder="e.g. 67000"
                type="number"
                value={formData.mileage}
                onChange={e => setFormData(f => ({ ...f, mileage: e.target.value }))}
              />
            </div>
            <div style={styles.field}>
              <label style={styles.fieldLabel}>ZIP Code</label>
              <input
                style={styles.input}
                placeholder="e.g. 90210"
                value={formData.zip}
                onChange={e => setFormData(f => ({ ...f, zip: e.target.value }))}
                maxLength={5}
              />
            </div>
            </div>
          </div>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <button style={styles.analyzeBtn} onClick={onSubmit}>
          Analyze my quote →
        </button>

        <p style={styles.disclaimer}>
          Results are opinions for informational purposes only. Not a substitute for professional mechanical advice.
        </p>
      </div>
    </main>
  );
}

// ─────────────────────────────────────────────
// LOADING STAGE
// ─────────────────────────────────────────────
function LoadingStage() {
  const steps = [
    { icon: '📄', text: 'Reading your quote...' },
    { icon: '🔍', text: 'Identifying line items...' },
    { icon: '📍', text: 'Checking regional pricing...' },
    { icon: '🧠', text: 'Analyzing necessity...' },
    { icon: '✅', text: 'Building your report...' }
  ];
  const [step, setStep] = useState(0);
  
  // Cycle through steps
  useState(() => {
    const interval = setInterval(() => {
      setStep(s => (s + 1) % steps.length);
    }, 1500);
    return () => clearInterval(interval);
  });

  return (
    <main style={{ ...styles.main, justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem', animation: 'pulse 1s infinite' }}>
          {steps[step].icon}
        </div>
        <div style={{ fontSize: '1.1rem', fontWeight: '500', color: '#0f0e0c', marginBottom: '0.5rem' }}>
          {steps[step].text}
        </div>
        <div style={{ fontSize: '0.85rem', color: '#9a9890' }}>Usually takes 15–30 seconds</div>
        <div style={styles.loadingBar}>
          <div style={{ ...styles.loadingFill, width: `${((step + 1) / steps.length) * 100}%` }} />
        </div>
      </div>
    </main>
  );
}

// ─────────────────────────────────────────────
// RESULTS STAGE
// ─────────────────────────────────────────────
function ResultsStage({ analysis, onReset }) {
  const { lineItems, summary, vehicle, mileage, negotiationTips } = analysis;

  return (
    <main style={{ ...styles.main, paddingTop: '5rem' }}>
      <div style={styles.resultsContainer}>

        {/* Header */}
        <div style={styles.resultsHeader}>
          <div>
            <h1 style={{ ...styles.h1, fontSize: '1.8rem', marginBottom: '0.25rem' }}>
              Your Quote Analysis
            </h1>
            {vehicle && (
              <p style={{ color: '#5a5850', fontSize: '0.9rem' }}>
                {vehicle.displayName || vehicle.make} · {mileage ? `${mileage.toLocaleString()} miles` : 'Mileage not provided'}
              </p>
            )}
          </div>
          <button style={styles.resetBtn} onClick={onReset}>Analyze another →</button>
        </div>

        {/* Summary cards */}
        <div style={styles.summaryGrid}>
          <SummaryCard label="Total quoted" value={`$${summary.totalQuoted.toLocaleString()}`} sub="by the shop" />
          <SummaryCard label="Estimated savings" value={`$${summary.estimatedSavings.toLocaleString()}`} sub="if you follow our advice" highlight />
          <SummaryCard label="Recommended total" value={`$${summary.recommendedTotal.toLocaleString()}`} sub="what you should pay" />
          <SummaryCard label="Items reviewed" value={lineItems.length} sub={`${summary.breakdown.skip + summary.breakdown.canWait} to skip or delay`} />
        </div>

        {/* Urgent alert */}
        {summary.hasUrgentItems && (
          <div style={styles.urgentAlert}>
            🚨 <strong>Safety notice:</strong> {summary.urgentItemNames.join(', ')} {summary.urgentItemNames.length === 1 ? 'requires' : 'require'} immediate attention. Do not skip.
          </div>
        )}

        {/* Line items */}
        <div style={styles.lineItemsSection}>
          <h2 style={styles.sectionTitle}>Line by line breakdown</h2>
          {lineItems.map(item => <LineItem key={item.id} item={item} />)}
        </div>

        {/* Negotiation tips */}
        {negotiationTips?.length > 0 && (
          <div style={styles.tipsSection}>
            <h2 style={styles.sectionTitle}>💬 What to say at the service desk</h2>
            <div style={styles.tipsList}>
              {negotiationTips.map((tip, i) => (
                <div key={i} style={styles.tip}>
                  <div style={styles.tipNumber}>{i + 1}</div>
                  <div style={styles.tipText}>{tip}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p style={{ ...styles.disclaimer, textAlign: 'left', marginTop: '2rem' }}>
          Fair Mechanic AI provides opinions for informational purposes only. Always consult a licensed mechanic for safety-related repairs. Results are based on publicly available pricing data and manufacturer maintenance schedules.
        </p>
      </div>
    </main>
  );
}

function SummaryCard({ label, value, sub, highlight }) {
  return (
    <div style={{ ...styles.summaryCard, ...(highlight ? styles.summaryCardHighlight : {}) }}>
      <div style={styles.summaryLabel}>{label}</div>
      <div style={{ ...styles.summaryValue, ...(highlight ? { color: '#1a7a4a' } : {}) }}>{value}</div>
      <div style={styles.summarySub}>{sub}</div>
    </div>
  );
}

function LineItem({ item }) {
  const [expanded, setExpanded] = useState(false);
  const config = VERDICT_CONFIG[item.verdict] || VERDICT_CONFIG.UNKNOWN;

  return (
    <div style={styles.lineItem} onClick={() => setExpanded(!expanded)}>
      <div style={styles.lineItemMain}>
        <div style={styles.lineItemLeft}>
          <span style={{ fontSize: '1.1rem' }}>{config.icon}</span>
          <div>
            <div style={styles.lineItemName}>{item.normalizedName || item.rawName}</div>
            {!expanded && (
              <div style={styles.lineItemPreview}>{item.explanation?.slice(0, 80)}...</div>
            )}
          </div>
        </div>
        <div style={styles.lineItemRight}>
          <div style={styles.lineItemPrice}>${item.totalPrice?.toLocaleString()}</div>
          <div style={{ ...styles.verdictTag, background: config.bg, color: config.color }}>
            {config.label}
          </div>
        </div>
      </div>

      {expanded && (
        <div style={styles.lineItemExpanded}>
          <p style={styles.explanation}>{item.explanation}</p>
          
          {item.priceVerdict?.fairPrice && (
            <div style={styles.priceBenchmark}>
              📊 Fair price in your area: <strong>${item.priceVerdict.fairPrice.toLocaleString()}</strong>
              {item.priceVerdict.overpayAmount > 0 && (
                <span style={{ color: '#c02020' }}> (you'd overpay by ${item.priceVerdict.overpayAmount})</span>
              )}
            </div>
          )}

          {item.diyPossible && item.diyNote && (
            <div style={styles.diyNote}>
              🔧 <strong>DIY option:</strong> {item.diyNote}
              {item.affiliateLinks && (
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <a href={item.affiliateLinks.amazon} target="_blank" rel="noopener noreferrer" style={styles.partLink}>
                    Amazon →
                  </a>
                  <a href={item.affiliateLinks.autozone} target="_blank" rel="noopener noreferrer" style={styles.partLink}>
                    AutoZone →
                  </a>
                  <a href={item.affiliateLinks.rockauto} target="_blank" rel="noopener noreferrer" style={styles.partLink}>
                    RockAuto →
                  </a>
                </div>
              )}
            </div>
          )}

          <div style={styles.urgencyRow}>
            Urgency: <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>{item.urgency}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────
const styles = {
  app: { fontFamily: "'DM Sans', system-ui, sans-serif", background: '#f9f7f3', minHeight: '100vh', color: '#0f0e0c' },
  nav: { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 2rem', background: 'rgba(249,247,243,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(15,14,12,0.08)' },
  navLogo: { fontFamily: 'Georgia, serif', fontSize: '1.3rem', fontWeight: '700', color: '#0f0e0c' },
  navLink: { fontSize: '0.875rem', color: '#5a5850', textDecoration: 'none' },
  navCta: { background: '#c8401a', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' },
  main: { paddingTop: '5rem', padding: '6rem 1.5rem 3rem', maxWidth: '760px', margin: '0 auto', boxSizing: 'border-box', width: '100%', overflow: 'hidden' },
  uploadContainer: { maxWidth: '640px', margin: '0 auto' },
  uploadHeader: { textAlign: 'center', marginBottom: '2rem' },
  h1: { fontFamily: 'Georgia, serif', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', lineHeight: '1.2', marginBottom: '0.75rem' },
  uploadSub: { fontSize: '1rem', color: '#5a5850', lineHeight: '1.6' },
  modeToggle: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', background: '#f0ede6', padding: '4px', borderRadius: '10px' },
  modeBtn: { flex: 1, padding: '0.5rem', border: 'none', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer', background: 'transparent', color: '#5a5850' },
  modeBtnActive: { background: 'white', color: '#0f0e0c', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  dropzone: { border: '2px dashed rgba(15,14,12,0.2)', borderRadius: '16px', padding: '3rem 2rem', textAlign: 'center', cursor: 'pointer', background: 'white', transition: 'all 0.2s', marginBottom: '1.5rem' },
  dropzoneActive: { borderColor: '#c8401a', background: '#fef5f2' },
  dropzoneFilled: { borderColor: '#1a7a4a', background: '#f0faf4', borderStyle: 'solid' },
  dropzoneTitle: { fontSize: '1rem', fontWeight: '600', marginTop: '0.5rem', marginBottom: '0.25rem' },
  dropzoneSub: { fontSize: '0.82rem', color: '#9a9890', marginBottom: '1rem' },
  fileBtn: { display: 'inline-block', background: '#c8401a', color: 'white', padding: '0.625rem 1.5rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '600', cursor: 'pointer' },
  changeFile: { display: 'inline-block', fontSize: '0.82rem', color: '#5a5850', cursor: 'pointer', textDecoration: 'underline' },
  textarea: { width: '100%', padding: '1rem', border: '1.5px solid rgba(15,14,12,0.15)', borderRadius: '12px', fontSize: '0.9rem', lineHeight: '1.6', resize: 'vertical', background: 'white', fontFamily: 'inherit', outline: 'none', marginBottom: '1.5rem', boxSizing: 'border-box' },
  optionalFields: { background: 'white', border: '1px solid rgba(15,14,12,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' },
  optionalLabel: { fontSize: '0.78rem', fontWeight: '600', color: '#9a9890', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.875rem' },
  fieldRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  fieldLabel: { fontSize: '0.78rem', fontWeight: '600', color: '#5a5850' },
  input: { padding: '0.5rem 0.75rem', border: '1px solid rgba(15,14,12,0.15)', borderRadius: '8px', fontSize: '0.875rem', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box', width: '100%', minWidth: 0 },
  errorBox: { background: '#fce8e8', color: '#c02020', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.875rem', marginBottom: '1rem' },
  analyzeBtn: { width: '100%', padding: '1rem', background: '#c8401a', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', marginBottom: '1rem' },
  disclaimer: { fontSize: '0.78rem', color: '#9a9890', textAlign: 'center', lineHeight: '1.6' },
  loadingBar: { width: '100%', height: '4px', background: '#f0ede6', borderRadius: '2px', marginTop: '1.5rem', overflow: 'hidden' },
  loadingFill: { height: '100%', background: '#c8401a', borderRadius: '2px', transition: 'width 1.4s ease' },
  resultsContainer: { maxWidth: '760px', margin: '0 auto' },
  resultsHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  resetBtn: { background: 'transparent', border: '1.5px solid rgba(15,14,12,0.15)', color: '#0f0e0c', padding: '0.5rem 1rem', borderRadius: '8px', fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '1.5rem' },
  summaryCard: { background: 'white', border: '1px solid rgba(15,14,12,0.08)', borderRadius: '12px', padding: '1rem' },
  summaryCardHighlight: { background: '#e2f2ea', border: '1px solid #b8dfc8' },
  summaryLabel: { fontSize: '0.75rem', color: '#9a9890', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.25rem' },
  summaryValue: { fontSize: '1.6rem', fontWeight: '700', color: '#0f0e0c', lineHeight: '1' },
  summarySub: { fontSize: '0.75rem', color: '#9a9890', marginTop: '0.25rem' },
  urgentAlert: { background: '#fce8e8', border: '1px solid #f0c0c0', borderRadius: '10px', padding: '0.875rem 1rem', marginBottom: '1.5rem', fontSize: '0.875rem' },
  lineItemsSection: { marginBottom: '2rem' },
  sectionTitle: { fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.75rem', color: '#0f0e0c' },
  lineItem: { background: 'white', border: '1px solid rgba(15,14,12,0.08)', borderRadius: '12px', padding: '1rem', marginBottom: '0.5rem', cursor: 'pointer', transition: 'border-color 0.2s' },
  lineItemMain: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' },
  lineItemLeft: { display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flex: 1 },
  lineItemName: { fontSize: '0.9rem', fontWeight: '600', marginBottom: '2px' },
  lineItemPreview: { fontSize: '0.78rem', color: '#9a9890' },
  lineItemRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  lineItemPrice: { fontSize: '1rem', fontWeight: '700' },
  verdictTag: { fontSize: '0.72rem', fontWeight: '700', padding: '3px 10px', borderRadius: '999px', whiteSpace: 'nowrap' },
  lineItemExpanded: { marginTop: '0.875rem', paddingTop: '0.875rem', borderTop: '1px solid rgba(15,14,12,0.07)' },
  explanation: { fontSize: '0.875rem', color: '#5a5850', lineHeight: '1.7', marginBottom: '0.75rem' },
  priceBenchmark: { fontSize: '0.82rem', background: '#f9f7f3', padding: '0.5rem 0.75rem', borderRadius: '8px', marginBottom: '0.5rem' },
  diyNote: { fontSize: '0.82rem', background: '#f0faf4', padding: '0.5rem 0.75rem', borderRadius: '8px', marginBottom: '0.5rem', lineHeight: '1.6' },
  partLink: { fontSize: '0.78rem', color: '#c8401a', textDecoration: 'none', fontWeight: '600' },
  urgencyRow: { fontSize: '0.78rem', color: '#9a9890', marginTop: '0.25rem' },
  tipsSection: { background: 'white', border: '1px solid rgba(15,14,12,0.08)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem' },
  tipsList: { display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.75rem' },
  tip: { display: 'flex', gap: '0.875rem', alignItems: 'flex-start' },
  tipNumber: { width: '24px', height: '24px', background: '#c8401a', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 },
  tipText: { fontSize: '0.875rem', color: '#5a5850', lineHeight: '1.6', paddingTop: '3px' }
};
 
